import { useLang } from 'hooks/lang';
import { useCallback, useMemo } from 'react';

import { Column, Row, Button, ResponsiveOverlay, LabeledSelect } from 'components/Blocks';
import { EntitySelect } from 'components/Entities';

import { FormControlDispatchAction } from 'hooks';

import { sortByName } from 'utils/array';
import { omitUndefined } from 'utils/object';
import { generateOptions } from './utils';

import { Filters, ListFilters, ManipulationState } from './types';

interface FilterModalProps<Entity> {
  isOpen: boolean;
  close: () => void;
  dispatch: FormControlDispatchAction<ManipulationState>;
  activeFilters: Filters;
  itens: Entity[];
  filters: ListFilters<Entity>[];
  hasFilter?: boolean;
}

interface SingleFilterProps<Entity> {
  filter: ListFilters<Entity>;
  activeFilters: Filters;
  onUpdate: (prop: string, newValue: string[] | string) => void;
  itens: Entity[];
  isOpen: boolean;
}

function SingleFilter<Entity>({
  activeFilters,
  filter,
  itens,
  onUpdate,
  isOpen,
}: SingleFilterProps<Entity>): JSX.Element {
  const { t } = useLang();

  const {
    getLabel,
    property,
    id,
    customSorter,
    multiple = true,
    optionExtractor,
    getOptionNode,
    getLoadConfig,
  } = filter;

  const options = useMemo(() => {
    const sorter = customSorter || sortByName;

    return sorter(generateOptions({ t, itens, optionExtractor, property }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, itens]);

  const loadConfig = useMemo(() => getLoadConfig?.(options), [options, getLoadConfig]);

  if (loadConfig) {
    return (
      // We are using it properly to shift between single/multiple values
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <EntitySelect
        {...loadConfig}
        id={`${property as string}-filter`}
        label={getLabel(t)}
        isMulti={multiple}
        labelKey="name"
        blockLoad={!isOpen || !!loadConfig?.blockLoad}
        formatLabel={getOptionNode}
        value={
          multiple
            ? [activeFilters[id ?? (property as string)] || []].flat()
            : typeof activeFilters[id ?? (property as string)] === 'string'
              ? activeFilters[id ?? (property as string)]
              : ''
        }
        onChangeDirect={(newFilter: string | string[]) =>
          onUpdate(id ?? (property as string), newFilter)
        }
      />
    );
  }

  return (
    // We are using it properly to shift between single/multiple values
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <LabeledSelect
      id={`${property as string}-filter`}
      options={options}
      label={getLabel(t)}
      isMulti={multiple}
      labelKey="name"
      formatLabel={getOptionNode}
      value={
        multiple
          ? [activeFilters[id ?? (property as string)] || []].flat()
          : typeof activeFilters[id ?? (property as string)] === 'string'
            ? activeFilters[id ?? (property as string)]
            : ''
      }
      onChangeDirect={(newFilter: string | string[]) =>
        onUpdate(id ?? (property as string), newFilter)
      }
    />
  );
}

function FilterModal<Entity>({
  close,
  isOpen,
  filters,
  itens,
  dispatch,
  activeFilters,
  hasFilter,
}: FilterModalProps<Entity>): JSX.Element {
  const { t } = useLang();

  const updateFilters = useCallback(
    (prop: string, newValue: string[] | string) => {
      const shouldRemove = !newValue.length;

      dispatch({
        filters: ({ filters: old }) =>
          omitUndefined({
            ...old,

            [prop]: shouldRemove ? undefined : newValue,
          }) as Filters,
      });
    },
    [dispatch],
  );

  return (
    <ResponsiveOverlay
      default="sheet"
      isOpen={isOpen}
      close={close}
      title={t('choose-your-filter') as string}
      className="overflow-y-auto"
      allowEscape
      description={
        hasFilter && (
          <Button
            size="small"
            variant="link"
            className="h-auto w-fit"
            onClick={() => dispatch({ filters: {} })}
          >
            {t('clear')}
          </Button>
        )
      }
    >
      <Column gap="8">
        <Column gap="6">
          {filters.map((filter) => (
            <SingleFilter
              key={filter.id ?? (filter.property as string)}
              filter={filter}
              activeFilters={activeFilters}
              itens={itens}
              isOpen={isOpen}
              onUpdate={updateFilters}
            />
          ))}
        </Column>

        <Row justifyContent="center">
          <Button onClick={close}>{t('close')}</Button>
        </Row>
      </Column>
    </ResponsiveOverlay>
  );
}

export default FilterModal;
